// Edge function client for company registry lookups (GUS, BDO).
// Replaces ad-hoc supabaseClient.functions.invoke("gus-regon-proxy" / "bdo-proxy")
// across useCompanyGusLookup, CompaniesList, CompanyView with typed methods.
//
// The underlying edge functions return `{ error?, <data fields> }` rather
// than the standard `{ success, data }` EdgeFnResponse shape, so these
// methods throw on error (rather than returning a discriminated union)
// and return the success fields directly.

import type { SupabaseClient } from '../base.ts';
import type { FunctionsInvoker } from './BaseEdgeFnClient.ts';

/** Raw body these proxies return: the result fields, plus `{ error }` on failure. */
type LookupEnvelope<T> = T & { error?: string };

// ── Result types ────────────────────────────────────────────────────────

export interface GusLookupResult {
  name?: string;
  short_name?: string;
  street?: string;
  building_number?: string;
  flat_number?: string;
  postal_code?: string;
  city?: string;
  country_code?: string;
  /** Optional soft-warnings from the GUS proxy (e.g. "company marked as suspended"). */
  warnings?: string[];
}

export interface BdoLookupResult {
  registration_number?: string;
}

// ── Client ──────────────────────────────────────────────────────────────

export class CompanyLookupEdgeFnClient {
  constructor(private readonly sb: SupabaseClient) {}

  /**
   * Look up a Polish company by NIP in the GUS REGON registry.
   * Returns the parsed company identity (name, address, BDO hint).
   * Throws on edge-function error or `{ error: ... }` response.
   */
  async gusLookup(nip: string): Promise<GusLookupResult> {
    const invoker = this.sb as unknown as FunctionsInvoker<LookupEnvelope<GusLookupResult>>;
    const { data, error } = await invoker.functions.invoke("gus-regon-proxy", {
      body: { nip },
    });
    if (error) throw new Error(error.message || "GUS lookup failed");
    if (data?.error) throw new Error(data.error);
    return data as GusLookupResult;
  }

  /**
   * Look up a Polish company's BDO registration number by NIP.
   * Returns `{ registration_number }` or `{}` if the company isn't in BDO.
   * Throws on edge-function error or `{ error: ... }` response.
   */
  async bdoLookup(nip: string): Promise<BdoLookupResult> {
    const invoker = this.sb as unknown as FunctionsInvoker<LookupEnvelope<BdoLookupResult>>;
    const { data, error } = await invoker.functions.invoke("bdo-proxy", {
      body: { nip },
    });
    if (error) throw new Error(error.message || "BDO lookup failed");
    if (data?.error) throw new Error(data.error);
    return data as BdoLookupResult;
  }
}
